import { GlobalIcon } from "get-life-storybook-ts";
import {
  NewCheckoutI,
  PurchasePeriodT,
} from "../../../../../../api/request/Lead/Interfaces/DataInterface.response";
import { numberFormatPayment } from "../../../../../../utils/Utils";
import { PricesV3T, PromotionV3T } from "../types";
import {
  formatPromoPrice,
  getDiscountPercent,
  getIntroMonthsPromo,
} from "../promoUtils";
import { chargedPriceForSelectedPeriod } from "../../../../../../utils/pricesV3";

interface FreqCardsProps {
  checkout: NewCheckoutI;
  prices?: PricesV3T;
  promotion?: PromotionV3T;
  translate: (key: string) => string;
  handlePurchasePeriod: (value: PurchasePeriodT) => void;
  variant?: "default" | "compact";
}

const FreqCards = ({
  checkout,
  prices,
  promotion,
  translate,
  handlePurchasePeriod,
  variant = "default",
}: FreqCardsProps) => {
  const period = (checkout.period?.toLowerCase() || "monthly") as
    | "monthly"
    | "yearly";

  return (
    <div className={`paymentV3FreqCards paymentV3FreqCards--${variant}`}>
      {(["yearly", "monthly"] as const).map((id) => {
        const active = period === id;
        const isYearlyCard = id === "yearly";
        const cardPrices = isYearlyCard ? prices?.yearly : prices?.monthly;
        const baseFull =
          cardPrices?.real ??
          (isYearlyCard ? checkout.finalYearlyPrice : checkout.regularPrice);
        // The selected period shows what the checkout will actually charge;
        // the other card relies on `prices` (both frequencies up front).
        const baseNow =
          (active ? chargedPriceForSelectedPeriod(checkout) : undefined) ??
          cardPrices?.promoted ??
          (isYearlyCard
            ? checkout.feeCollection?.find((f) => f.frecuency === "YEARLY")
                ?.total ?? checkout.finalYearlyPrice
            : checkout.priceToPay);
        const cardDiscounted = baseNow < baseFull;

        // Intro-months promos (e.g. ES "3MESES1E") only apply to the monthly
        // frequency: show "first N months at X" + "then Y/month" instead of a
        // misleading strikethrough.
        const introPromo = !isYearlyCard
          ? getIntroMonthsPromo(checkout, promotion, baseFull, baseNow)
          : undefined;
        // Absolute promos (intro-months codes) translated to a percentage give
        // non-round numbers ("−24%") that read as an error: for those, the
        // yearly card shows the absolute saving instead. The % label is kept
        // for genuinely percentage promos, where it comes out round.
        const isAbsolutePromo = !!getIntroMonthsPromo(
          checkout,
          promotion,
          prices?.monthly?.real ?? checkout.regularPrice,
          prices?.monthly?.promoted ?? checkout.priceToPay
        );
        const yearlySaving =
          isYearlyCard && cardDiscounted && isAbsolutePromo
            ? baseFull - baseNow
            : 0;
        // Permanent percentage promos (e.g. IT "10_sconto") show the regular
        // price struck through plus the "-X%" badge on BOTH cards.
        const discountPct =
          cardDiscounted && !isAbsolutePromo
            ? getDiscountPercent(baseFull, baseNow)
            : 0;

        return (
          <button
            key={id}
            type="button"
            onClick={() => handlePurchasePeriod(id as PurchasePeriodT)}
            className={`paymentV3FreqCards__card ${
              active ? "paymentV3FreqCards__card--active" : ""
            }`}
          >
            <span className="paymentV3FreqCards__label">
              {translate(
                isYearlyCard
                  ? "paymentV3.summary.freq.annual"
                  : "paymentV3.summary.freq.monthly"
              )}
            </span>

            {introPromo ? (
              <>
                <span className="paymentV3FreqCards__price">
                  {introPromo.months
                    ? translate("paymentV3.summary.freq.introMonths")
                        .replace("{months}", String(introPromo.months))
                        .replace(
                          "{price}",
                          `${formatPromoPrice(introPromo.introPrice)} €`
                        )
                    : translate("paymentV3.summary.freq.introGeneric")}
                </span>
                <span className="paymentV3FreqCards__sub">
                  {translate("paymentV3.summary.freq.introAfter").replace(
                    "{price}",
                    `${numberFormatPayment(introPromo.afterPrice)} €`
                  )}
                </span>
              </>
            ) : (
              <>
                {discountPct > 0 && (
                  <span className="paymentV3FreqCards__oldPrice">
                    {numberFormatPayment(baseFull)} €
                  </span>
                )}
                <span className="paymentV3FreqCards__price">
                  {numberFormatPayment(baseNow)} €
                </span>
                <span className="paymentV3FreqCards__sub">
                  {translate(
                    isYearlyCard
                      ? "paymentV3.summary.freq.perYear"
                      : "paymentV3.summary.freq.perMonth"
                  )}
                </span>
                {discountPct > 0 && (
                  <span className="paymentV3FreqCards__discount">
                    {translate("paymentV3.summary.freq.discountLabel").replace(
                      "{percent}",
                      String(discountPct)
                    )}
                  </span>
                )}
                {yearlySaving > 0 && (
                  <span className="paymentV3FreqCards__discount">
                    {translate(
                      "paymentV3.summary.freq.savingsAbsolute"
                    ).replace(
                      "{amount}",
                      `${numberFormatPayment(yearlySaving)} €`
                    )}
                  </span>
                )}
              </>
            )}

            {active && (
              <span className="paymentV3FreqCards__check">
                <GlobalIcon iconName="CheckedIcon" size="XS" color="#FFFFFF" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default FreqCards;
